import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { GraduationCap, PlayCircle, FileText, CheckCircle2, ListChecks, BarChart3, BookOpen, Check, Crown, Layers, Award, ClipboardList, ArrowRight, Megaphone, Pin, Send, Trash2 } from 'lucide-react'
import { courseApi, assignmentApi, channelApi, type CourseProgress, type CourseRating, type CertificateInfo, type AssignmentWithMine, type Announcement, type ClassInfo } from '../api/lessonBuilderApi'
import { COURSE_LEVELS, GRADE_LABEL, courseGrade, dueStatus, fmtDue, DUE_TONE_CLASS, type CourseDoc } from '../types/CourseDoc'
import { CalendarClock } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import StarRating from '../components/StarRating'

const levelLabel = (lv?: string) => COURSE_LEVELS.find((x) => x.value === lv)?.label || ''

// Trang khoá học công khai (giao diện học sinh): bìa + giới thiệu + lộ trình bài học.
const CourseViewerPage: React.FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [course, setCourse] = useState<CourseDoc | null | undefined>(undefined)
  const [progress, setProgress] = useState<CourseProgress | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [rating, setRating] = useState<CourseRating | null>(null)
  const [cert, setCert] = useState<CertificateInfo | null>(null)
  const [issuing, setIssuing] = useState(false)
  const [assignments, setAssignments] = useState<AssignmentWithMine[]>([])
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [classes, setClasses] = useState<ClassInfo[]>([])
  const [annForm, setAnnForm] = useState({ title: '', body: '', class_name: '', is_pinned: false })
  const [postingAnn, setPostingAnn] = useState(false)

  const loadProgress = (courseId: string) => courseApi.progress(courseId).then(setProgress).catch(() => {})
  const loadCert = (courseId: string) => courseApi.certificateInfo(courseId).then(setCert).catch(() => {})

  useEffect(() => {
    if (!slug) return
    courseApi.getBySlug(slug).then((c) => {
      setCourse(c)
      if (c) {
        loadProgress(c.id)
        loadCert(c.id)
        assignmentApi.courseAssignments(c.id).then(setAssignments).catch(() => {})
        channelApi.announcements(c.id).then(setAnnouncements).catch(() => {})
        courseApi.myClasses().then(setClasses).catch(() => {})
        courseApi.getRating(c.id).then(setRating).catch(() => {})
      }
    }).catch(() => setCourse(null))
  }, [slug])

  // Nhận / xem chứng chỉ hoàn thành (server cấp khi đã hoàn thành 100%).
  const handleCertificate = async () => {
    if (!course) return
    if (cert?.certificate?.code) { navigate(`/chung-chi/${cert.certificate.code}`); return }
    setIssuing(true)
    try {
      const c = await courseApi.issueCertificate(course.id)
      navigate(`/chung-chi/${c.code}`)
    } catch (e) {
      const msg = (e as Error)?.message || ''
      alert(msg.includes('400') ? 'Bạn cần hoàn thành 100% khoá học để nhận chứng chỉ.' : 'Chưa cấp được chứng chỉ, vui lòng thử lại.')
    } finally {
      setIssuing(false)
    }
  }

  const handleRate = async (stars: number) => {
    if (!course) return
    try {
      setRating(await courseApi.rate(course.id, stars))
    } catch {
      alert('Cần đăng nhập để đánh giá khoá học.')
    }
  }

  const handleEnroll = async () => {
    if (!course) return
    setEnrolling(true)
    try {
      await courseApi.enroll(course.id)
      await loadProgress(course.id)
    } catch (e) {
      const msg = (e as Error)?.message || ''
      alert(msg.includes('403') || msg.toLowerCase().includes('pro')
        ? 'Khoá học này yêu cầu tài khoản PRO. Vui lòng nâng cấp để ghi danh.'
        : 'Ghi danh chưa thành công. Hãy đăng nhập rồi thử lại.')
    } finally {
      setEnrolling(false)
    }
  }

  const isCourseOwner = !!course && !!user && (user.role === 'admin' || user.id === course.ownerId)

  const fmtDateTime = (value?: string) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const handlePostAnnouncement = async () => {
    if (!course || postingAnn) return
    const title = annForm.title.trim()
    if (title.length < 3) { alert('Tiêu đề thông báo cần ít nhất 3 ký tự.'); return }
    setPostingAnn(true)
    try {
      const created = await channelApi.createAnnouncement(course.id, {
        title,
        body: annForm.body.trim(),
        class_name: annForm.class_name || undefined,
        is_pinned: annForm.is_pinned,
      })
      setAnnouncements((prev) => [created, ...prev])
      setAnnForm({ title: '', body: '', class_name: '', is_pinned: false })
    } catch {
      alert('Chưa đăng được thông báo. Hãy kiểm tra quyền giáo viên hoặc đăng nhập lại.')
    } finally {
      setPostingAnn(false)
    }
  }

  const handleRemoveAnnouncement = async (id: string) => {
    if (!confirm('Ẩn thông báo này khỏi bảng tin?')) return
    try {
      await channelApi.removeAnnouncement(id)
      setAnnouncements((prev) => prev.filter((a) => a.id !== id))
    } catch {
      alert('Chưa xoá được thông báo.')
    }
  }
  if (course === undefined) return <div className="p-10 text-center text-gray-400">Đang tải...</div>
  if (!course) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Không tìm thấy khoá học.</p>
        <Link to="/teacher/khoa-hoc" className="text-blue-600 font-semibold">← Về danh sách khoá học</Link>
      </div>
    )
  }

  const lessons = course.lessonSummaries || []
  const totalCards = lessons.reduce((s, l) => s + (l.cardCount || 0), 0)
  const outcomes = course.outcomes || []
  const requirements = course.requirements || []
  const doneIds = new Set((progress?.lessons || []).filter((x) => x.completed).map((x) => x.id))
  const ratAvg = rating?.avg ?? course.ratingAvg ?? 0
  const ratCount = rating?.count ?? course.ratingCount ?? 0

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-2.5 text-xs text-gray-500 flex items-center gap-1.5 overflow-hidden">
          <Link to="/" className="hover:text-blue-600 shrink-0">Trang chủ</Link><span>/</span>
          <Link to="/khoa-hoc" className="hover:text-blue-600 shrink-0">Khoá học</Link><span>/</span>
          <span className="text-gray-700 font-semibold truncate">{course.title}</span>
        </div>
      </div>
      {/* Top: ảnh bìa lớn (trái) + thẻ thông tin & đăng ký (phải) — kiểu chuvanbien */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-5 pb-2 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Banner ảnh bìa */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-gray-100 bg-gradient-to-br from-blue-600 to-indigo-700">
            {course.coverImage ? (
              <img src={course.coverImage} alt={course.title} className="w-full h-56 md:h-80 object-cover" />
            ) : (
              <div className="h-56 md:h-80 flex flex-col items-center justify-center text-white px-6 text-center">
                <GraduationCap size={48} className="mb-3 opacity-80" />
                <span className="text-2xl font-black">{course.title}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold">{GRADE_LABEL(courseGrade(course)).split(' (')[0]}</span>
            {course.category && <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">{course.category}</span>}
            {levelLabel(course.level) && <span className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-bold flex items-center gap-1"><BarChart3 size={12} /> {levelLabel(course.level)}</span>}
          </div>
        </div>

        {/* Thẻ thông tin + đăng ký */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 lg:sticky lg:top-4">
            <h1 className="text-xl font-black text-gray-900 leading-snug">{course.title}</h1>
            {course.subtitle && <p className="text-sm text-gray-500 mt-1">{course.subtitle}</p>}
            {ratCount > 0 && <div className="mt-2"><StarRating value={ratAvg} count={ratCount} size={16} /></div>}

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-gray-500">Truy cập</span>
              <span className={`text-lg font-black flex items-center gap-1 ${course.access === 'pro' ? 'text-amber-600' : 'text-emerald-600'}`}>
                {course.access === 'pro' ? <><Crown size={16} /> Gói PRO</> : 'Miễn phí'}
              </span>
            </div>

            {progress?.enrolled && progress.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs font-bold text-gray-500 mb-1"><span>Tiến độ</span><span className="text-emerald-600">{progress.completed}/{progress.total} · {progress.percent}%</span></div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress.percent}%` }} /></div>
              </div>
            )}

            {/* Hạn hoàn thành + trạng thái nhắc */}
            {course.dueDate && (() => {
              const ds = dueStatus(course.dueDate, { completed: (progress?.percent || 0) >= 100 })
              if (!ds) return null
              return (
                <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold ${DUE_TONE_CLASS[ds.tone]}`}>
                  <CalendarClock size={16} className="shrink-0" />
                  <span>Hạn hoàn thành: {fmtDue(course.dueDate)}</span>
                  <span className="ml-auto text-xs font-bold">{ds.label}</span>
                </div>
              )
            })()}

            <div className="mt-4">
              {progress?.enrolled ? (
                <button disabled={lessons.length === 0} onClick={() => navigate(`/bai-giang/${progress.nextSlug || lessons[0]?.slug}`)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40">
                  <PlayCircle size={20} /> {progress.percent >= 100 ? 'Học lại' : progress.completed > 0 ? 'Học tiếp' : 'Bắt đầu học'}
                </button>
              ) : progress ? (
                <button disabled={lessons.length === 0 || enrolling} onClick={handleEnroll}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-40">
                  <GraduationCap size={20} /> {enrolling ? 'Đang ghi danh...' : 'Ghi danh học'}
                </button>
              ) : (
                <button disabled={lessons.length === 0} onClick={() => lessons[0] && navigate(`/bai-giang/${lessons[0].slug}`)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40">
                  <PlayCircle size={20} /> Bắt đầu học
                </button>
              )}
            </div>
            {/* Chứng chỉ hoàn thành — hiện khi đã có chứng chỉ hoặc đã hoàn thành 100% */}
            {(cert?.certificate || cert?.eligible) && (
              <button
                onClick={handleCertificate}
                disabled={issuing}
                className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 font-bold shadow hover:brightness-105 disabled:opacity-50"
              >
                <Award size={20} /> {issuing ? 'Đang cấp chứng chỉ…' : cert?.certificate ? 'Xem chứng chỉ' : 'Nhận chứng chỉ hoàn thành'}
              </button>
            )}

            {course.access === 'pro' && !progress?.enrolled && (
              <a href="/pro-register" className="block text-center text-xs font-bold text-amber-600 mt-2 hover:underline">Chưa có PRO? Kích hoạt gói PRO →</a>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2.5 text-sm">
              <div className="flex items-center gap-2 text-gray-600"><Layers size={16} className="text-gray-400 shrink-0" /> Chương: <span className="font-bold text-gray-800 ml-auto">{(course.chapters?.length || 0)} chương</span></div>
              <div className="flex items-center gap-2 text-gray-600"><BookOpen size={16} className="text-gray-400 shrink-0" /> Bài giảng: <span className="font-bold text-gray-800 ml-auto">{lessons.length} bài</span></div>
              <div className="flex items-center gap-2 text-gray-600"><FileText size={16} className="text-gray-400 shrink-0" /> Mục nội dung: <span className="font-bold text-gray-800 ml-auto">{totalCards}</span></div>
              {levelLabel(course.level) && <div className="flex items-center gap-2 text-gray-600"><BarChart3 size={16} className="text-gray-400 shrink-0" /> Trình độ: <span className="font-bold text-gray-800 ml-auto">{levelLabel(course.level)}</span></div>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cột chính: giới thiệu + lộ trình */}
        <div className="lg:col-span-2 space-y-6">
          {course.description && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-black text-gray-900 mb-3">Giới thiệu khoá học</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{course.description}</p>
            </section>
          )}

          {(announcements.length > 0 || isCourseOwner) && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Megaphone size={20} className="text-orange-600" />
                <h2 className="text-lg font-black text-gray-900">Bảng tin chính thức</h2>
                <span className="ml-auto text-xs font-bold text-gray-400">{announcements.length} thông báo</span>
              </div>

              {isCourseOwner && (
                <div className="mb-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      value={annForm.title}
                      onChange={(e) => setAnnForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Tiêu đề thông báo"
                      className="md:col-span-2 px-3 py-2 rounded-xl border border-orange-100 bg-white text-sm outline-none focus:ring-2 focus:ring-orange-200"
                    />
                    <select
                      value={annForm.class_name}
                      onChange={(e) => setAnnForm((f) => ({ ...f, class_name: e.target.value }))}
                      className="px-3 py-2 rounded-xl border border-orange-100 bg-white text-sm outline-none focus:ring-2 focus:ring-orange-200"
                    >
                      <option value="">Toàn khoá</option>
                      {classes.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <textarea
                    value={annForm.body}
                    onChange={(e) => setAnnForm((f) => ({ ...f, body: e.target.value }))}
                    placeholder="Nội dung thông báo, tài liệu cần chuẩn bị, hạn việc..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-orange-100 bg-white text-sm outline-none focus:ring-2 focus:ring-orange-200 resize-y"
                  />
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-xs font-bold text-orange-800">
                      <input type="checkbox" checked={annForm.is_pinned} onChange={(e) => setAnnForm((f) => ({ ...f, is_pinned: e.target.checked }))} />
                      Ghim thông báo
                    </label>
                    <button
                      onClick={handlePostAnnouncement}
                      disabled={postingAnn}
                      className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-black hover:bg-orange-700 disabled:opacity-50"
                    >
                      <Send size={15} /> {postingAnn ? 'Đang đăng...' : 'Đăng bảng tin'}
                    </button>
                  </div>
                </div>
              )}

              {announcements.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Chưa có thông báo nào cho khoá học này.</p>
              ) : (
                <div className="space-y-3">
                  {announcements.map((a) => (
                    <article key={a.id} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${a.is_pinned ? 'bg-orange-100 text-orange-700' : 'bg-white text-gray-400 border border-gray-100'}`}>
                          {a.is_pinned ? <Pin size={16} /> : <Megaphone size={16} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-black text-gray-900">{a.title}</h3>
                            {a.class_name && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold">{a.class_name}</span>}
                            {a.is_pinned && <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">Đã ghim</span>}
                          </div>
                          {a.body && <p className="mt-1.5 text-sm text-gray-600 whitespace-pre-line leading-relaxed">{a.body}</p>}
                          <p className="mt-2 text-[11px] text-gray-400">{fmtDateTime(a.created_at)}</p>
                        </div>
                        {isCourseOwner && (
                          <button onClick={() => handleRemoveAnnouncement(a.id)} title="Ẩn thông báo" className="p-2 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
          {outcomes.length > 0 && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
                <ListChecks size={20} className="text-emerald-600" /> Bạn sẽ học được gì
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {outcomes.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Bài tập tự luận của khoá */}
          {assignments.length > 0 && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
                <ClipboardList size={20} className="text-blue-600" /> Bài tập tự luận
              </h2>
              <div className="space-y-2.5">
                {assignments.map((as) => {
                  const sub = as.mySubmission
                  const status = sub?.status === 'graded'
                    ? { t: `Đã chấm: ${sub.score}/${as.max_score}đ`, c: 'text-emerald-600' }
                    : sub ? { t: 'Đã nộp · chờ chấm', c: 'text-amber-600' }
                    : { t: 'Chưa nộp', c: 'text-gray-400' }
                  return (
                    <button key={as.id} onClick={() => navigate(`/bai-tap/${as.slug || as.id}`)}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 transition">
                      <FileText size={18} className="text-blue-500 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-bold text-gray-800 truncate">{as.title}</span>
                        <span className={`block text-xs font-bold ${status.c}`}>{status.t}</span>
                      </span>
                      <ArrowRight size={16} className="text-gray-300 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </section>
          )}

          {/* Lộ trình bài học */}
          <section className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-black text-gray-900 mb-4">Nội dung khoá học</h2>
            {lessons.length === 0 ? (
              <p className="text-center text-gray-400 py-6">Khoá học chưa có bài giảng.</p>
            ) : (() => {
              // Nhóm bài theo CHƯƠNG / mục Ôn tập (khoá cũ không có chapters → 1 nhóm).
              const byId = new Map(lessons.map((l) => [l.id, l] as const))
              const groups = (course.chapters && course.chapters.length)
                ? course.chapters
                : [{ id: 'all', title: '', kind: 'chapter' as const, lessonIds: lessons.map((l) => l.id) }]
              const multi = groups.length > 1
              return (
                <div className="space-y-5">
                  {groups.map((ch) => {
                    const isReview = ch.kind === 'review'
                    const items = ch.lessonIds.map((id) => byId.get(id)).filter(Boolean) as typeof lessons
                    if (!items.length && multi) return null
                    return (
                      <div key={ch.id}>
                        {multi && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{isReview ? '📘' : '📂'}</span>
                            <h3 className={`font-black ${isReview ? 'text-amber-700' : 'text-gray-800'}`}>{ch.title}</h3>
                            {isReview && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 rounded-full px-2 py-0.5">Ôn tập</span>}
                            <span className="text-xs text-gray-400 ml-auto">{items.length} bài</span>
                          </div>
                        )}
                        <div className="space-y-3">
                          {items.map((l) => {
                            const i = lessons.findIndex((x) => x.id === l.id)
                            const done = doneIds.has(l.id)
                            return (
                              <button
                                key={l.id}
                                onClick={() => navigate(`/bai-giang/${l.slug}`)}
                                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                                  done ? 'bg-emerald-50/50 border-emerald-100 hover:border-emerald-300' : 'bg-gray-50/60 border-gray-100 hover:border-blue-200 hover:bg-blue-50/40'
                                }`}
                              >
                                <span className={`w-9 h-9 rounded-full font-bold flex items-center justify-center shrink-0 ${
                                  done ? 'bg-emerald-500 text-white' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {done ? <Check size={18} /> : i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-gray-900 truncate">{l.title}</p>
                                  <p className="text-xs text-gray-400 flex items-center gap-1">
                                    <FileText size={12} /> {l.cardCount} mục nội dung{done ? ' · đã hoàn thành' : ''}
                                  </p>
                                </div>
                                {done ? <CheckCircle2 className="text-emerald-500 shrink-0" size={22} /> : <PlayCircle className="text-blue-500 shrink-0" size={22} />}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </section>
        </div>

        {/* Cột phụ: yêu cầu + bắt đầu học */}
        <aside className="lg:col-span-1 space-y-6">
          {/* Đánh giá sao */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-base font-black text-gray-900 mb-2">Đánh giá</h2>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl font-black text-gray-900">{ratAvg > 0 ? ratAvg.toFixed(1) : '—'}</span>
              <StarRating value={ratAvg} count={ratCount} />
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500 mb-1">{rating?.myStars ? `Bạn đã đánh giá ${rating.myStars} sao` : 'Đánh giá của bạn:'}</p>
              <StarRating value={rating?.myStars || 0} size={24} onRate={handleRate} />
            </div>
          </div>

          {(course.instructorName || course.instructorSchool || course.instructorExperience) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-3">Giảng viên</h2>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-black text-lg shrink-0">
                  {(course.instructorName || 'GV').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  {course.instructorName && <p className="font-bold text-gray-900">{course.instructorName}</p>}
                  {course.instructorSchool && <p className="text-xs text-gray-500">{course.instructorSchool}</p>}
                  {course.instructorExperience && <p className="text-sm text-gray-600 mt-1.5 leading-relaxed whitespace-pre-line">{course.instructorExperience}</p>}
                </div>
              </div>
            </div>
          )}

          {requirements.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-3">Yêu cầu đầu vào</h2>
              <ul className="space-y-2">
                {requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0 mt-1.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

export default CourseViewerPage
